import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { AdSchedule } from '@jwp/ott-common/types/ad-schedule';
import type { PlaylistItem, Source } from '@jwp/ott-common/types/playlist';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { deepCopy } from '@jwp/ott-common/src/utils/collection';
import { logDev, testId } from '@jwp/ott-common/src/utils/common';
import useEventCallback from '@jwp/ott-hooks-react/src/useEventCallback';
import useOttAnalytics from '@jwp/ott-hooks-react/src/useOttAnalytics';
import { attachAnalyticsParams } from '@jwp/ott-common/src/utils/analytics';
import env from '@jwp/ott-common/src/env';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { useMediaSources } from '@jwp/ott-hooks-react/src/useMediaSources';
import { useTranslation } from 'react-i18next';
import { useGADeviceType } from '@jwp/ott-common/src/utils/GADeviceType';
import ReactGA from 'react-ga4';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';

import type { JWPlayer } from '../../../types/jwplayer';
import { addScript } from '../../utils/dom';
import NextCountdownOverlay from '../NextCountdownOverlay/NextCountdownOverlay';

import styles from './Player.module.scss';
import { useWatchHistoryListener } from '@jwp/ott-hooks-react/src/useWatchHistoryListener';
import { CONTENT_TYPE, S73_CDN_PATH } from '@jwp/ott-common/src/constants';
import { sendGAEvent } from '@jwp/ott-hooks-react/src/useAnalytics';
import { isLiveEvent, MediaStatus } from '@jwp/ott-common/src/utils/liveEvent';
import { isLiveChannel } from '@jwp/ott-common/src/utils/media';
import { useVideoProgressThresholds } from '@jwp/ott-hooks-react/src/useVideoProgressThreshold';
import { formatTime } from '../../utils/formatTime';
import { getSSAIAdState, useJWAdTimer } from './PlayerHelper';
import { useCustomVolumeSlider } from '@jwp/ott-hooks-react/src/useCustomVolumeSlider';
type Props = {
  feedId?: string;
  item: PlaylistItem;
  startTime?: number;
  autostart?: boolean;
  adsData?: AdSchedule;
  onReady?: (player?: JWPlayer) => void;
  onPlay?: () => void;
  onPause?: () => void;
  onComplete?: () => void;
  onUserActive?: () => void;
  onUserInActive?: () => void;
  onBeforePlay?: () => void;
  onFirstFrame?: () => void;
  onRemove?: () => void;
  onNext?: () => void;
  onPlaylistItem?: () => void;
  onPlaylistItemCallback?: (item: PlaylistItem) => Promise<undefined | PlaylistItem>;
  videoThumnail?: string;
  nextItem?: PlaylistItem;
  isAutoPlayNextVideo?: boolean;
  ReConnecting: string;
};

interface S73Wrapper {
  wrapPlayer: (player: any) => void;
  wrapPlayerConfig: (config: any) => void;
  destroy?: () => void;
}

const Player: React.FC<Props> = ({
  item,
  adsData,
  onReady,
  onPlay,
  onPause,
  onComplete,
  onUserActive,
  onUserInActive,
  onBeforePlay,
  onFirstFrame,
  onRemove,
  onPlaylistItem,
  onPlaylistItemCallback,
  onNext,
  feedId,
  startTime = 0,
  autostart,
  videoThumnail,
  nextItem,
  isAutoPlayNextVideo,
  ReConnecting,
}: Props) => {
  const params = new URLSearchParams(window.location.search);
  const isShared = params.get('shared') === 'true';
  const { t } = useTranslation(['video']);

  const playerElementRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<JWPlayer>();
  const captionsOverrideInitializedRef = useRef(false);
  const captionsObserverRef = useRef<MutationObserver | null>(null);
  const { injectVolumeSlider } = useCustomVolumeSlider(playerRef);

  const s73wrapperRef = useRef<S73Wrapper | null>(null);
  const loadingRef = useRef(false);
  const [libLoaded, setLibLoaded] = useState(!!window.jwplayer);
  const startTimeRef = useRef(startTime);
  const [adCountdown, setAdCountdown] = useState(false);
  const [adCountdownTime, setAdCountdownTime] = useState<number | string>();
  const activeAdRef = useRef<{ offset: number; duration: number } | null>(null);
  const [nextCountdownTime, setNextCountdownTime] = useState<number | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [showNextOverlay, setShowNextOverlay] = useState(true);
  const nextVideoInTime = 10;
  const [showLoader, setShowLoader] = useState(false);
  const lastPlaybackRef = useRef<{ position: number; duration: number }>({ position: 0, duration: 0 });
  const isLiveContent = item.mediaStatus === MediaStatus.LIVE || isLiveEvent(item) || isLiveChannel(item);
  const { getProgressData } = useVideoProgressThresholds(playerRef, lastPlaybackRef, isLiveContent, item.mediaid);

  const filteredItem = {
    ...item,
    images: item.images.filter((image) => image.type !== 'video/mp4'),
  };

  const setIsOverlayManuallyClosedRef = useRef(false);

  const { adConfigLevel, isAdEnabled, isAnalyticsEnable, system73AccessKey } = useAppConfig();

  const user = useAccountStore((state) => state.user);
  const isLoggedIn = !!user;
  const userId = isLoggedIn ? user?.id : 'Guest user';
  const deviceType = useGADeviceType();

  const setPlayer = useOttAnalytics(item, feedId);
  const { sources, ssaiAdsData } = useMediaSources({ item, baseUrl: env.APP_API_BASE_URL });

  const ssaiAdsDataRef = useRef(ssaiAdsData);

  useEffect(() => {
    ssaiAdsDataRef.current = ssaiAdsData;
  }, [ssaiAdsData]);

  const { settings, config } = useConfigStore((s) => s);
  const ids = config.menu.map((m) => m?.custom?.drmMediaId || m?.custom?.drmPlaylistId).filter((e) => e !== undefined) as string[];

  const isDRMContent = ids.includes(item.mediaid) || ids.includes(item.feedid) || ids.includes(feedId ?? '');

  useWatchHistoryListener(playerRef.current, item, undefined);

  const playerId = settings.playerId;
  const playerLicenseKey = settings.playerLicenseKey;
  const isAdfigLevelPresent = adConfigLevel !== 'none' && adConfigLevel != null && adConfigLevel != undefined && adConfigLevel != '';

  const formatDurationTime = (seconds: number | undefined) => {
    if (seconds == undefined) {
      return 'N/A';
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  const handleBeforePlay = useEventCallback(onBeforePlay);
  const handlePlay = useEventCallback(onPlay);
  const handlePause = useEventCallback(onPause);
  const handleComplete = useEventCallback(onComplete);
  const handleUserActive = useEventCallback(onUserActive);
  const handleUserInactive = useEventCallback(onUserInActive);
  const handleFirstFrame = useEventCallback(() => {
    // a11y: auto focus to play/pause button
    const playerContainer = playerRef.current?.getContainer();
    const playPauseButton = playerContainer?.querySelector(
      '.jw-controlbar [aria-label="Play"], .jw-button-container [aria-label="Pause"]',
    ) as HTMLDivElement | null;
    playPauseButton?.focus();

    onFirstFrame?.();
  });

  const handleRemove = useEventCallback(() => {
    const pos = lastPlaybackRef.current.position;
    const rawDur = playerRef.current?.getDuration?.() ?? lastPlaybackRef.current.duration ?? 0;
    const progressPercent = pos > 0 ? +Math.floor((pos / rawDur) * 100).toFixed(2) : 0;
    const { shouldSend, threshold } = getProgressData();
    onRemove?.();
    if (isAnalyticsEnable) {
      const eventData = {
        video_id: item?.mediaid,
        video_title: item?.title,
        video_type: isLiveContent ? 'Live' : 'VOD',
        userid_no: userId,
        device_type: deviceType,
        video_progress: `${threshold}%`,
      };
      // Only add progress if it's not live and position is not null/undefined
      if (!isLiveContent && shouldSend) {
        sendGAEvent('video_progress', eventData);
      }
    }
  });

  const handlePlaylistItem = useEventCallback(onPlaylistItem);
  const handlePlaylistItemCallback = useEventCallback(onPlaylistItemCallback);
  const handleNextClick = useEventCallback(onNext);
  const handleReady = useEventCallback(() => onReady && onReady(playerRef.current));

  const seekBlockedRef = useRef(false);
  const keydownListenerContainerRef = useRef<HTMLElement | null>(null);

  const forceBack = () => {
    const player = playerRef.current;
    if (!player) return;

    player.seek(player.getPosition());
  };

  const disableSeeking = () => {
    if (seekBlockedRef.current) return;

    seekBlockedRef.current = true;
    const container = playerRef.current?.getContainer?.();

    // Disable seekbar interaction
    document.querySelector('.jw-slider-time')?.style.setProperty('pointer-events', 'none');
    playerRef.current?.on('seek', forceBack);
  };

  const enableSeeking = () => {
    if (!seekBlockedRef.current) return;

    seekBlockedRef.current = false;

    document.querySelector('.jw-slider-time')?.style.setProperty('pointer-events', 'auto');
    playerRef.current?.off('seek', forceBack);
  };

  const handlePlayerKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.code === 'Space' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();

      const player = playerRef.current;
      if (!player) return;

      const state = player.getState();

      if (state === 'playing') {
        player.pause(true);
      } else if (state === 'paused') {
        player.play();
      } else {
        console.warn('[Spacebar] Ignored due to player state:', state);
      }
    }
  }, []);

  useEffect(() => {
    const blockSeekByKeys = (event: KeyboardEvent) => {
      if (!seekBlockedRef.current) return;
      const el = event.target as HTMLElement;
      const isTextInput = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
      if (isTextInput) return;
      const seekKeys = ['ArrowLeft', 'ArrowRight'];
      const isSeekKey = seekKeys.includes(event.code) || (event.key >= '0' && event.key <= '9');
      if (isSeekKey) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
    };
    document.addEventListener('keydown', blockSeekByKeys, true);
    return () => document.removeEventListener('keydown', blockSeekByKeys, true);
  }, []);

  const attachEvents = useCallback(() => {
    playerRef.current?.on('error', (event: { code?: number; message?: string; source?: string; [key: string]: unknown }) => {
      setShowLoader(false);
      if (isAnalyticsEnable) {
        sendGAEvent('video_error', {
          video_id: item?.mediaid,
          video_title: item?.title,
          error_code: event.code ?? null,
          error_message: event.message ?? null,
          video_type: isLiveContent ? 'Live' : 'VOD',
          userid_no: userId,
          device_type: deviceType,
        });
      }
    });
    let prevQualityIndex: number | null = null;
    playerRef.current?.on('levels', (event) => {
      prevQualityIndex = event?.currentQuality;
      const levels = playerRef.current?.getQualityLevels();
      if (!levels || levels.length === 0) return;
      const userPreference = localStorage.getItem('videoQuality') ?? 'Auto';
      if (userPreference === 'Auto') {
        playerRef.current?.setCurrentQuality(-1);
      } else {
        const parseLabel = (label: string) => parseInt(label.replace(/[^\d]/g, ''), 10);

        const preferredHeight = parseLabel(userPreference);
        const sortedLevels = [...levels].sort((a, b) => parseLabel(b.label) - parseLabel(a.label));
        const bestFitIndex = sortedLevels.findIndex((level) => parseLabel(level.label) <= preferredHeight);
        const selectedIndex = bestFitIndex >= 0 ? levels.findIndex((l) => l.label === sortedLevels[bestFitIndex].label) : 0;
        playerRef.current?.setCurrentQuality(selectedIndex);
      }
    });

    playerRef.current?.on('ready', () => {
      playerRef.current?.getContainer()?.focus();
      playerRef.current?.play();
      setShowLoader(true);
      const bufferIcon = playerRef.current?.getContainer()?.querySelector('.jw-display-icon-display .jw-icon .jw-svg-icon-buffer') as HTMLElement;
      if (bufferIcon) {
        bufferIcon.style.display = 'none';
      }
    });
    playerRef.current?.on('beforePlay', () => {
      handleBeforePlay?.();
      setShowLoader(true);
      const bufferIcon = playerRef.current?.getContainer()?.querySelector('.jw-display-icon-display .jw-icon .jw-svg-icon-buffer') as HTMLElement;
      if (bufferIcon) {
        bufferIcon.style.display = 'none';
      }
    });
    playerRef.current?.on('complete', () => {
      handleComplete?.();
      if (isAnalyticsEnable) {
        const position = playerRef.current?.getPosition();
        const duration = playerRef.current?.getDuration();
        const progress = duration && position ? Number(position / duration) * 100 : 100;
        sendGAEvent('video_complete', {
          video_id: item?.mediaid,
          video_title: item?.title,
          watched_percentage: `${progress}%`,
          video_type: isLiveContent ? 'Live' : 'VOD',
          userid_no: userId,
          device_type: deviceType,
        });
      }
    });
    playerRef.current?.on('play', () => {
      const { position, shouldSend, threshold } = getProgressData();

      handlePlay?.();
      setShowLoader(false);
      if (isAnalyticsEnable) {
        const eventData = {
          video_id: item?.mediaid,
          video_title: item?.title,
          autoplay: autostart,
          duration: item?.duration,
          video_type: isLiveContent ? 'Live' : 'VOD',
          userid_no: userId,
          device_type: deviceType,
        };

        if (shouldSend) {
          eventData.video_progress = `${threshold}%`;
        }

        sendGAEvent('video_play', eventData);
      }
    });
    playerRef.current?.on('pause', () => {
      const { position, shouldSend, threshold } = getProgressData();
      handlePause?.();
      const eventData = {
        video_id: item?.mediaid,
        video_title: item?.title,
        video_type: isLiveContent ? 'Live' : 'VOD',
        userid_no: userId,
        device_type: deviceType,
      };

      // Only add progress if it's not live and position is not null/undefined
      if (isAnalyticsEnable) {
        if (shouldSend) {
          eventData.video_progress = `${threshold}%`;
        }
        sendGAEvent('video_pause', eventData);
      }
    });

    playerRef.current?.on('seek', (event) => {
      const { position, shouldSend, threshold } = getProgressData();

      if (isAnalyticsEnable) {
        const fromPosition = event?.position;
        const toPosition = event?.offset;
        const direction = toPosition > fromPosition;
        const eventData = {
          video_id: item?.mediaid,
          video_title: item?.title,
          video_type: isLiveContent ? 'Live' : 'VOD',
          from_position: formatDurationTime(fromPosition),
          to_position: formatDurationTime(toPosition),
          userId_no: userId,
          device_type: deviceType,
        };
        if (shouldSend) {
          eventData.video_progress = `${threshold}%`;
        }
        sendGAEvent(direction ? 'video_forward' : 'video_rewind', eventData);
      }
    });

    playerRef.current?.on('userActive', handleUserActive);
    playerRef.current?.on('userInactive', handleUserInactive);
    playerRef.current?.on('firstFrame', () => {
      handleFirstFrame?.();
    });
    playerRef.current?.on('remove', handleRemove);
    playerRef.current?.on('playlistItem', handlePlaylistItem);
    playerRef.current?.on('nextClick', handleNextClick);

    // // Captions label override: "und" → "English" (UI-only). JW writes "und" at unpredictable times.
    // // Run override on an interval so we correct labels regardless of when JW updates the DOM.
    const applyCaptionsUILabelOverride = () => {
      const player = playerRef.current;
      if (!player) return;

      const container = player.getContainer?.();
      if (!container) return;

      container.querySelectorAll('.jw-settings-content-item, .jw-icon-cc .jw-text').forEach((el) => {
        const text = el.textContent?.trim();
        if (!text || text === 'und') {
          el.textContent = config?.custom?.defaultCaptionTitle ? (config?.custom?.defaultCaptionTitle as string) : 'English';
        }
      });
    };

    playerRef.current?.on('ready', () => {
      const container = playerRef?.current?.getContainer();

      captionsObserverRef.current = new MutationObserver(applyCaptionsUILabelOverride);

      captionsObserverRef.current.observe(container as any, {
        subtree: true,
        childList: true,
        characterData: true,
      });

      applyCaptionsUILabelOverride();

      injectVolumeSlider();
    });

    playerRef.current?.on('adBreakStart' as any, () => {
      setShowLoader(false);
      setAdCountdown(true);
    });

    playerRef.current?.on('adTime', (e) => {
      if (!e.duration) return;

      const remaining = Math.max(0, Math.ceil(e.duration - e.position));
      // setAdCountdownTime(formatTime(remaining));

      if (remaining <= 0) {
        setAdCountdown(false);
      }
    });

    playerRef.current?.on('adBreakEnd' as any, () => {
      setAdCountdown(false);
    });

    playerRef.current?.on('time', (event) => {
      lastPlaybackRef.current = {
        position: event.position,
        duration: event.duration,
      };

      const currentTime = Math.floor(event.position);
      const currentAdsData = ssaiAdsDataRef.current;
      const duration = Math.floor(event.duration);

      const timeRemaining = duration - currentTime;

      
      if (timeRemaining <= nextVideoInTime && timeRemaining > 0) {
        if (!setIsOverlayManuallyClosedRef.current) {
          setNextCountdownTime(Math.ceil(timeRemaining));
          setShowNextOverlay(true);
        }
      } else {
        if (nextCountdownTime !== null || showNextOverlay) {
          setNextCountdownTime(null);
          setShowNextOverlay(false);
          setIsOverlayManuallyClosedRef.current = false;
        }
      }

      if (ssaiAdsDataRef.current) {
        const ssaiAd = getSSAIAdState(event.position, ssaiAdsDataRef.current);
        
        setAdCountdownTime(ssaiAd.remaining);
        setAdCountdown(ssaiAd.isAd);
        if (ssaiAd.isAd) {
          disableSeeking();
        } else {
          enableSeeking();
        }
      }
      // const ad = currentAdsData?.breaks?.find((adItem: any) => currentTime >= adItem.time_offset && currentTime < adItem.time_offset + adItem.duration);
      // if (ad) {
      //   if (!activeAdRef.current || activeAdRef.current.offset !== ad.time_offset) {
      //     activeAdRef.current = {
      //       offset: ad.time_offset,
      //       duration: Math.floor(ad.duration),
      //     };
      //     setAdCountdown(true);
      //   }

      //   const timeLeft = Math.ceil(activeAdRef.current.offset + activeAdRef.current.duration - currentTime);
      //   setAdCountdownTime(formatTime(timeLeft));
      // } else {
      //   if (activeAdRef.current) {
      //     activeAdRef.current = null;
      //     setAdCountdown(false);
      //   }
      // }
    });
    playerRef.current?.setPlaylistItemCallback(handlePlaylistItemCallback);
    let ReconnectingIconHTML: string | null = null;

    playerRef.current?.on('buffer', (event) => {
      const isTemporaryInterruption = event.reason === 'stalled';
      if (isTemporaryInterruption && (item?.contentType == CONTENT_TYPE.liveChannel || item.contentType == CONTENT_TYPE.live)) {
        const iconEl = document.querySelector('.jw-icon.jw-icon-display') as HTMLElement;
        if (iconEl) {
          if (!ReconnectingIconHTML) ReconnectingIconHTML = iconEl.innerHTML;
          iconEl.innerHTML = `<span style="font-size:24px;">${ReConnecting}</span>`;
        }
        return;
      }
      setShowLoader(true);
      if (isAnalyticsEnable) {
        const position = playerRef.current?.getPosition();
        sendGAEvent('video_buffer_start', {
          video_id: item?.mediaid,
          video_title: item?.title,
          position: formatDurationTime(position),
          video_type: isLiveContent ? 'Live' : 'VOD',
          userid_no: userId,
          device_type: deviceType,
        });
      }
    });

    playerRef.current?.on('bufferFull' as any, () => {
      const iconEl = document.querySelector('.jw-icon.jw-icon-display') as HTMLElement;
      if (iconEl && ReconnectingIconHTML) {
        iconEl.innerHTML = ReconnectingIconHTML;
        ReconnectingIconHTML = null;
      }
      if (isAnalyticsEnable) {
        const position = playerRef.current?.getPosition();
        sendGAEvent('video_buffer_end', {
          video_id: item?.mediaid,
          video_title: item?.title,
          position: formatDurationTime(position),
          video_type: isLiveContent ? 'Live' : 'VOD',
          userid_no: userId,
          device_type: deviceType,
        });
      }
      playerRef.current?.play();
    });

    playerRef.current?.on('mute', (event: any) => {
      if (isAnalyticsEnable) {
        if (event.mute) {
          sendGAEvent('video_mute_toggle', {
            video_id: item?.mediaid,
            video_title: item?.title,
            muted: true,
            video_type: isLiveContent ? 'Live' : 'VOD',
            userid_no: userId,
            device_type: deviceType,
          });
        } else {
          sendGAEvent('video_mute_toggle', {
            video_id: item?.mediaid,
            video_title: item?.title,
            muted: false,
            video_type: isLiveContent ? 'Live' : 'VOD',
            userid_no: userId,
            device_type: deviceType,
          });
        }
      }
    });

    playerRef.current?.on('levelsChanged', (event: any) => {
      const levels = event?.levels;
      const newIndex = event?.currentQuality;
      const newLevel = levels[newIndex];
      const oldLevel = prevQualityIndex !== null ? levels[prevQualityIndex] : null;
      if (isAnalyticsEnable) {
        sendGAEvent('video_quality_change', {
          video_id: item?.mediaid,
          video_title: item?.title,
          old_quality: oldLevel?.label,
          new_quality: newLevel?.label,
          video_type: isLiveContent ? 'Live' : 'VOD',
          userid_no: userId,
          device_type: deviceType,
        });
      }
    });

    playerRef.current?.on('fullscreen', (event: any) => {
      if (isAnalyticsEnable) {
        if (event?.fullscreen) {
          sendGAEvent('video_fullscreen', {
            video_id: item?.mediaid,
            video_title: item?.title,
            video_type: isLiveContent ? 'Live' : 'VOD',
            userid_no: userId,
            device_type: deviceType,
          });
        } else {
          sendGAEvent('video_exit_fullscreen', {
            video_id: item?.mediaid,
            video_title: item?.title,
            video_type: isLiveContent ? 'Live' : 'VOD',
            userid_no: userId,
            device_type: deviceType,
          });
        }
      }
    });

    const container = playerRef.current?.getContainer?.();
    if (container) {
      keydownListenerContainerRef.current = container;
      container.addEventListener('keydown', handlePlayerKeyDown as EventListener, true);
    }
  }, [
    handleReady,
    handleBeforePlay,
    handleComplete,
    handlePlay,
    handlePause,
    handleUserActive,
    handleUserInactive,
    handleFirstFrame,
    handleRemove,
    handlePlaylistItem,
    handleNextClick,
    handlePlaylistItemCallback,
    handlePlayerKeyDown,
  ]);

  const detachEvents = useCallback(() => {
    const keydownContainer = keydownListenerContainerRef.current;
    if (keydownContainer) {
      keydownContainer.removeEventListener('keydown', handlePlayerKeyDown as EventListener, true);
      keydownListenerContainerRef.current = null;
    }

    playerRef.current?.removePlaylistItemCallback?.();

    // disconnect MutationObserver
    captionsObserverRef?.current?.disconnect();
    captionsObserverRef.current = null;

    playerRef.current?.off('error');
    playerRef.current?.off('levels');
    playerRef.current?.off('ready');
    playerRef.current?.off('beforePlay');
    playerRef.current?.off('complete');
    playerRef.current?.off('play');
    playerRef.current?.off('pause');
    playerRef.current?.off('seek');
    playerRef.current?.off('userActive');
    playerRef.current?.off('userInactive');
    playerRef.current?.off('firstFrame');
    playerRef.current?.off('remove');
    playerRef.current?.off('playlistItem');
    playerRef.current?.off('nextClick');
    playerRef.current?.off('captionsList' as any);
    playerRef.current?.off('captionsChanged' as any);
    playerRef.current?.off('controls' as any);
    playerRef.current?.off('adBreakStart' as any);
    playerRef.current?.off('adTime');
    playerRef.current?.off('adBreakEnd' as any);
    playerRef.current?.off('time');
    playerRef.current?.off('buffer');
    playerRef.current?.off('bufferFull' as any);
    playerRef.current?.off('mute');
    playerRef.current?.off('levelsChanged');
    playerRef.current?.off('fullscreen');
  }, [handlePlayerKeyDown]);

  //system 73 params
  const s73SdkConfig = {
    apiKey: system73AccessKey,
  };

  // Helper function to force HLS-only sources
  function forceHlsSource(item: any) {
    // Deep copy to avoid mutation
    const newItem = deepCopy(item);

    if (newItem.sources && Array.isArray(newItem.sources)) {
      // Filter only HLS sources if present
      const hlsSources = newItem.sources.filter((src: any) => src.file?.includes('.m3u8'));

      // If no HLS sources exist, try converting DASH to HLS
      newItem.sources =
        hlsSources.length > 0
          ? hlsSources
          : newItem.sources.map((src: any) => ({
              ...src,
              file: src.file?.replace('.mpd', '.m3u8'),
              type: 'application/vnd.apple.mpegurl',
            }));
    } else if (newItem.file) {
      // In case the source is defined as `file`
      newItem.file = newItem.file.replace('.mpd', '.m3u8');
      newItem.type = 'application/vnd.apple.mpegurl';
    }

    return newItem;
  }

  // Player options are untyped
  const playerOptions: { [key: string]: unknown } = {
    advertising: {
      ...(isAdfigLevelPresent || isAdEnabled ? adsData : {}),
      // Beta feature
      showCountdown: true,
    },
    captions: {
      default: false,
      fontSize: 14,
      color: '#FFFFFF',
      backgroundOpacity: 0,
      edgeStyle: 'raised',
    },
    timeSlider: {
      showAdMarkers: true,
    },
    aspectratio: false,
    controls: true,
    displaytitle: false,
    displayHeading: false,
    displaydescription: false,
    floating: {
      mode: 'never',
    },
    height: '100%',
    mute: isShared ? true : false,
    playbackRateControls: true,
    pipIcon: 'disabled',
    playlist: isDRMContent
      ? [deepCopy({ ...filteredItem, starttime: startTimeRef.current, feedid: feedId, image: videoThumnail })]
      : [
          forceHlsSource({
            ...filteredItem,
            starttime: startTimeRef.current,
            feedid: feedId,
            image: videoThumnail,
          }),
        ],
    repeat: false,
    cast: {},
    stretching: 'uniform',
    width: '100%',
    nextupDisplay: { show: false },
  };

  useEffect(() => {
    if (!window.jwplayer && !loadingRef.current) {
      loadingRef.current = true;

      // Always load JW Player SDK
      const playerScript = document.createElement('script');
      playerScript.src = `${env.APP_API_BASE_URL}/libraries/${playerId}.js`;
      playerScript.type = 'application/javascript';
      playerScript.onload = () => {
        setLibLoaded(true);
        loadingRef.current = false;
      };
      document.body.appendChild(playerScript);

      // load System73 SDK
      if (system73AccessKey) {
        const sdkScript = document.createElement('script');
        sdkScript.src = S73_CDN_PATH;
        sdkScript.type = 'application/javascript';
        document.body.appendChild(sdkScript);
      }
    }
  }, [playerId, system73AccessKey]);

  useEffect(() => {
    // Update the startTimeRef each time the startTime changes
    startTimeRef.current = startTime;
  }, [startTime]);

  useEffect(() => {
    const loadPlaylist = () => {
      if (!item || !playerRef.current) {
        return;
      }

      const currentItem = playerRef.current?.getPlaylistItem() as unknown as PlaylistItem | null;

      // We already loaded this item
      if (currentItem && currentItem.mediaid === item.mediaid) {
        return;
      }

      // Update autostart parameter
      if (typeof autostart !== 'undefined') {
        playerRef.current?.setConfig({ autostart });
      }

      // Load new item
      const playlistItem = isDRMContent
        ? deepCopy({
            ...filteredItem,
            starttime: startTimeRef.current,
            feedid: feedId,
            image: videoThumnail,
          })
        : forceHlsSource({
            ...filteredItem,
            starttime: startTimeRef.current,
            feedid: feedId,
            image: videoThumnail,
          });

      if (isDRMContent) {
        detachEvents();
        playerRef.current.setup({
          ...playerOptions,
          playlist: [playlistItem],
        });
        attachEvents();
      } else {
        playerRef.current.load([playlistItem]);
      }

      setTimeout(() => {
        playerRef.current?.play();
      }, 100);
    };

    const initializePlayer = () => {
      if (!window.jwplayer || !playerElementRef.current) return;

      playerRef.current = window.jwplayer(playerElementRef.current) as JWPlayer;

      // Inject user_id and profile_id into the CDN analytics
      // @todo this currently depends on stores
      attachAnalyticsParams(item);

      // Only set the autostart parameter when it is defined or it will override the player.defaults autostart setting
      if (typeof autostart !== 'undefined') {
        playerOptions.autostart = autostart;
      }

      // Set the license key if provided
      if (playerLicenseKey) {
        playerOptions.key = playerLicenseKey;
      }

      const doSetup = () => {
        if (!playerRef.current) return;
        playerRef.current.setup(playerOptions);
        setPlayer(playerRef.current);
        attachEvents();
      };

      const tryWrapS73 = () => {
        if (window.S73JwplayerWrapper && playerRef.current && !s73wrapperRef.current) {
          s73wrapperRef.current = window.S73JwplayerWrapper(s73SdkConfig);
          s73wrapperRef.current.wrapPlayerConfig(playerOptions);
          s73wrapperRef.current.wrapPlayer(playerRef.current);
          doSetup();
        } else if (!s73wrapperRef.current) {
          setTimeout(tryWrapS73, 50);
        }
      };

      if (system73AccessKey) {
        tryWrapS73();
      } else {
        doSetup();
      }
    };

    if (playerRef.current) {
      return loadPlaylist();
    }

    if (libLoaded) {
      initializePlayer();
    }
  }, [libLoaded, item, detachEvents, attachEvents, playerId, setPlayer, autostart, adsData, sources, playerLicenseKey, feedId]);

  useEffect(() => {
    return () => {
      try {
        if (playerRef.current) {
          playerRef.current.stop();
          detachEvents();
          playerRef.current.remove();
          s73wrapperRef.current?.destroy?.();
          playerRef.current = undefined;
          s73wrapperRef.current = null;
          lastPlaybackRef.current = { position: 0, duration: 0 };
        }

        if (countdownTimerRef.current) {
          clearInterval(countdownTimerRef.current);
          countdownTimerRef.current = null;
        }
      } catch (err) {
        console.error('[Player Cleanup Error]', err);
      } finally {
        playerRef.current = undefined;
        s73wrapperRef.current = null;
      }
    };
  }, [detachEvents]);

  const setupCaptionsOverride = () => {
    const player = playerRef.current;
    if (!player || captionsOverrideInitializedRef.current) return;

    captionsOverrideInitializedRef.current = true;

    const override = () => {
      try {
        const container = player.getContainer?.();
        if (!container) return;

        // Override captions menu items
        container.querySelectorAll('.jw-settings-content-item').forEach((el, index) => {
          if (index === 0) return;
          const text = el.textContent?.trim();
          if (!text || text === 'und') {
            el.textContent = config?.custom?.defaultCaptionTitle ? (config?.custom?.defaultCaptionTitle as string) : 'English';
          }
        });

        // Override CC button label
        const ccText = container.querySelector('.jw-icon-cc .jw-text');
        if (ccText) {
          const text = ccText.textContent?.trim();
          if (!text || text === 'und') {
            ccText.textContent = config?.custom?.defaultCaptionTitle ? (config?.custom?.defaultCaptionTitle as string) : 'English';
          }
        }
      } catch (e) {
        // silent fail (production safe)
      }
    };

    // JW lifecycle events where captions UI can change
    player.on('ready', override);
    player.on('play', override);
    player.on('captionsList', override);
    player.on('captionsChanged', override);

    override();
  };

  // Call after player is initialized
  setupCaptionsOverride();

  const handleCloseOverlay = () => {
    setShowNextOverlay(false);
    setIsOverlayManuallyClosedRef.current = true;
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setNextCountdownTime(null);
  };

  const handleSkipToEnd = () => {
    if (playerRef.current) {
      const player = playerRef.current;
      const duration = player.getDuration();
      if (player.getState() === 'playing') {
        player.seek(duration - 0.1);
      } else {
        const onPlay = () => {
          player.seek(duration - 0.1);
          player.off('play', onPlay);
        };
        player.on('play', onPlay);
        player.play();
      }
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const liveEl = document.querySelector('.jw-text-live.jw-dvr-live') as HTMLElement | null;

      if (!liveEl) return;

      if (liveEl.textContent?.trim() === 'Not Live') {
        liveEl.textContent = 'Go Live';
      }
    }, 500);

    return () => clearInterval(interval);
  }, []);

  (function hideJWStatusText() {
    const hide = () => {
      document.querySelectorAll('.jwplayer .jw-text[role="status"]').forEach((el) => {
        el.style.visibility = 'hidden';
        el.style.opacity = '0';
      });
    };

    const observer = new MutationObserver(hide);

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    hide();
  })();

  useJWAdTimer();

  console.log('isAutoPlayNextVideo', isAutoPlayNextVideo);
  console.log('nextItem', nextItem);
  console.log('nextCountdownTime', nextCountdownTime);
  console.log('showNextOverlay', showNextOverlay);
  return (
    <div className={`${styles.container} ${adCountdown ? styles.adPlaying : ''}`} data-testid={testId('player-container')}>
      <div ref={playerElementRef} tabIndex={0} />
      {showLoader && (
        <div className={styles.loader}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" focusable="false">
            <path d="M120,186.667a66.667,66.667,0,0,1,0-133.333V40a80,80,0,1,0,80,80H186.667A66.846,66.846,0,0,1,120,186.667Z"></path>
          </svg>
        </div>
      )}
      {adCountdownTime && (
        <div className="adCountdownOverlay">
          <span>Ad</span> {adCountdownTime}
        </div>
      )}
      {isAutoPlayNextVideo && nextItem && nextCountdownTime !== null && showNextOverlay && (
        <NextCountdownOverlay
          nextCountdownTime={nextCountdownTime}
          handleCloseOverlay={handleCloseOverlay}
          handleSkipToEnd={handleSkipToEnd}
          nextVideoInTime={nextVideoInTime}
          nextItem={nextItem}
        />
      )}
    </div>
  );
};

export default Player;
